import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IonicModule, ModalController, NavParams } from '@ionic/angular';

import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { SUPPORT_STEP_ASSIGNEE_TYPES } from 'src/app/constants';
import { Customer, SlackChannel } from 'src/models';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';

@Component({
  selector: 'app-triage',
  templateUrl: './triage.component.html',
  styleUrls: ['./triage.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, RouterModule, ChannelSelectorModule],
})
export class TriageComponent {
  conversationId: string;
  channel: SlackChannel;
  shouldCreateChannel = false;
  isChannelPrivate = true;
  message: string;
  mentionId: string;
  mentionType: string;
  mentionLabel: string;
  channelNamePrefix = 'triage';
  isLoading = false;

  constructor(
    private authService: AuthService,
    private conversationsService: ConversationsService,
    private customersService: CustomersService,
    private msgService: MessageService,
    private slackService: SlackService,
    private searchablePopoverService: SearchablePopoverService,
    private modalCtrl: ModalController,
    navParams: NavParams,
  ) {
    const customerId = navParams.get('customerId');
    if (navParams.get('shouldLoadDefaultTriageChannel')) {
      this.loadTriageChannel(customerId);
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  async triage() {
    if (!this.shouldCreateChannel && !this.channel?.id) {
      this.msgService.show('Please select a channel first');
      return;
    }

    this.isLoading = true;

    const createChannel = this.shouldCreateChannel
      ? {
          prefix: this.channelNamePrefix,
          isPrivate: true,
        }
      : null;

    try {
      const { message } = await this.conversationsService.triage(
        this.conversationId,
        createChannel ? null : this.channel?.id,
        this.message,
        this.mentionId,
        createChannel,
      );
      this.isLoading = false;
      this.modalCtrl.dismiss({ message });
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  onSlackChannelChanged(slackChannel?: SlackChannel) {
    this.channel = slackChannel;
  }

  async loadTriageChannel(customerId?: string) {
    // Default. Likely to switch to a project-level default at some point
    let channelId = this.authService.tenant.defaultTriageChannelId;

    // Customers also can specify a default that overrides
    if (customerId) {
      const customer = await this.customersService.get(customerId);
      if (customer?.defaultTriageChannelId) {
        channelId = customer.defaultTriageChannelId;
      }
    }

    const defaultTriageChannel = await this.slackService.getChannel(channelId);

    if (defaultTriageChannel) {
      this.channel = defaultTriageChannel;
    }
  }

  showAssigneeOptions() {
    this.searchablePopoverService.assignees({
      event,
      selectedId: this.mentionId,
      showPrimaryRepOption: false,
      showCreatedByUserOption: false,
      includeNonAgents: true,
      onlyMentionable: true,
      showNoneOption: true,
      callback: async (_id: string, name: string, object: any) => {
        this.mentionId = object.id;
        this.mentionType = object?.type ?? SUPPORT_STEP_ASSIGNEE_TYPES.USER;
        this.mentionLabel = name;
      },
    });
  }

  learnMore() {
    window.open('https://docs.unthread.io/conversations/triaging', '_blank');
  }

  channelCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  visibilityCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }
}
